import React, { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Building2, ShieldCheck, CheckCircle2, PhoneCall, Search,
  Wrench, Home, ClipboardList, BarChart3, Rocket, Star,
  ChevronRight, FileCheck, CreditCard, Lightbulb, Menu, X,
  MapPin, Mail, ArrowRight, TrendingUp, Award, Users, Zap
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
interface CardProps { icon: React.ReactNode; title: string; desc: string; colorClass?: string; }
interface ServiceProps { icon: React.ReactNode; title: string; points: string[]; colorClass?: string; }
interface KPIProps { num: string; label: string; colorClass?: string; }
interface FieldProps { label: string; type: string; placeholder?: string; value: string; onChange: (v: string) => void; }

// ─── Animation helpers ────────────────────────────────────────────────────────
const fadeUp = { hidden: { opacity: 0, y: 28 }, visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as const } } };
const stagger = (delay = 0) => ({ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay, ease: "easeOut" as const } } });

function AnimSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp} className={className}>
      {children}
    </motion.div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function MetronavixLanding() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [formStatus, setFormStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on nav click
  const handleNavClick = () => setMenuOpen(false);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) return;
    setFormStatus("sending");
    // Replace with your real form endpoint (e.g. Formspree, AWS SES API, etc.)
    try {
      await new Promise(r => setTimeout(r, 1200)); // Simulate API call
      setFormStatus("sent");
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      setFormStatus("error");
    }
  };

  const [legalOpen, setLegalOpen] = useState(false);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#why", label: "Why Us" },
    { href: "#process", label: "Process" },
    { href: "#testimonials", label: "Proof" },
    { href: "#contact", label: "Contact" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-900">

      {/* ── NAVBAR ─────────────────────────────────────────────────────────── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" : "bg-white/80 backdrop-blur border-b border-transparent"
        }`}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-lg" aria-label="Metronavix – Home">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-md">
                <Building2 className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              <span className="font-bold tracking-tight text-lg bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">
                Metronavix
              </span>
            </a>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-6 text-sm" role="navigation" aria-label="Main navigation">
              {navLinks.map(l => (
                <a key={l.href} href={l.href} className="text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded">
                  {l.label}
                </a>
              ))}

              {/* Legal dropdown */}
              <div className="relative">
                <button
                  onClick={() => setLegalOpen(o => !o)}
                  className="flex items-center gap-1 text-gray-600 hover:text-gray-900 font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                  aria-haspopup="true"
                  aria-expanded={legalOpen}
                >
                  Legal <ChevronRight className={`w-3.5 h-3.5 transition-transform duration-200 ${legalOpen ? "rotate-90" : ""}`} aria-hidden="true" />
                </button>
                {legalOpen && (
                  <>
                    {/* Invisible overlay to close on outside click */}
                    <div className="fixed inset-0 z-40" onClick={() => setLegalOpen(false)} />
                    <div className="absolute top-full right-0 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-1.5 z-50">
                      <a
                        href="/terms/privacy-policy"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      >
                        <span className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 text-xs">🔒</span>
                        Privacy Policy
                      </a>
                      <a
                        href="/terms"
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                      >
                        <span className="w-6 h-6 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 text-xs">📋</span>
                        Terms & Conditions
                      </a>
                    </div>
                  </>
                )}
              </div>

              <a
                href="#contact"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                aria-label="Talk to us – book a free consultation"
              >
                <PhoneCall className="w-4 h-4" aria-hidden="true" /> Talk to us
              </a>
            </nav>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              onClick={() => setMenuOpen(o => !o)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div id="mobile-menu" className="md:hidden bg-white border-t border-gray-100 shadow-lg" role="navigation" aria-label="Mobile navigation">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map(l => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={handleNavClick}
                  className="py-3 px-4 text-gray-700 font-medium rounded-xl hover:bg-blue-50 hover:text-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {l.label}
                </a>
              ))}

              {/* Legal links in mobile */}
              <div className="my-1 border-t border-gray-100 pt-2">
                <p className="px-4 py-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">Legal</p>
                <a
                  href="/terms/privacy-policy"
                  onClick={handleNavClick}
                  className="flex items-center gap-2 py-3 px-4 text-gray-700 font-medium rounded-xl hover:bg-blue-50 hover:text-blue-700 transition-colors"
                >
                  🔒 Privacy Policy
                </a>
                <a
                  href="/terms"
                  onClick={handleNavClick}
                  className="flex items-center gap-2 py-3 px-4 text-gray-700 font-medium rounded-xl hover:bg-purple-50 hover:text-purple-700 transition-colors"
                >
                  📋 Terms & Conditions
                </a>
              </div>

              <a
                href="#contact"
                onClick={handleNavClick}
                className="mt-2 py-3 px-4 text-center rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
              >
                Book Free Consultation
              </a>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" aria-labelledby="hero-heading">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50/60 to-pink-50" aria-hidden="true" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" aria-hidden="true" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center py-20 lg:py-28">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold tracking-wide uppercase mb-5">
                <MapPin className="w-3 h-3" aria-hidden="true" /> PropTech · Hyderabad → Pan‑India
              </div>
              <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight">
                Own Better.{" "}
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Real Estate,
                </span>{" "}
                Reinvented.
              </h1>
              <p className="mt-6 text-gray-600 text-lg leading-relaxed max-w-xl">
                Metronavix brings verified property discovery, builder‑grade QA/QC inspections, and post‑handover support — so you buy with confidence and live with peace of mind.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  <Rocket className="w-5 h-5" aria-hidden="true" /> Get a free consultation
                </a>
                <a
                  href="#services"
                  className="inline-flex items-center gap-2 rounded-xl px-6 py-3.5 border-2 border-gray-200 font-semibold text-gray-700 hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  Explore services <ChevronRight className="w-4 h-4" aria-hidden="true" />
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-5 text-sm font-medium">
                <div className="flex items-center gap-1.5 text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
                  <ShieldCheck className="w-4 h-4" aria-hidden="true" /> RERA‑aware
                </div>
                <div className="flex items-center gap-1.5 text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full">
                  <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> 75+ QC checks
                </div>
                <div className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-3 py-1.5 rounded-full">
                  <BarChart3 className="w-4 h-4" aria-hidden="true" /> Data insights
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
              <div className="rounded-2xl border border-gray-100 bg-white/80 backdrop-blur shadow-xl p-5 grid grid-cols-2 gap-4">
                <Card icon={<Search className="w-5 h-5" />} title="Verified Listings" desc="No fake promises. Full documentation & site‑visit due diligence." colorClass="bg-gradient-to-br from-blue-500 to-cyan-500" />
                <Card icon={<ClipboardList className="w-5 h-5" />} title="Quality Inspections" desc="75+ point checklist, snag reports, photo/video evidence." colorClass="bg-gradient-to-br from-green-500 to-emerald-500" />
                <Card icon={<Wrench className="w-5 h-5" />} title="Post‑handover Support" desc="Defect closure coordination with builders & vendors." colorClass="bg-gradient-to-br from-orange-500 to-red-500" />
                <Card icon={<Home className="w-5 h-5" />} title="Interiors & Loans" desc="Design‑build interiors and home‑loan assistance." colorClass="bg-gradient-to-br from-purple-500 to-pink-500" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ───────────────────────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 py-10" aria-label="Key metrics">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { icon: <Award className="w-6 h-6" />, num: "75+", label: "QC Checks per Unit" },
            { icon: <TrendingUp className="w-6 h-6" />, num: ">200", label: "Snags Resolved" },
            { icon: <Zap className="w-6 h-6" />, num: "<48h", label: "Avg. Report TAT" },
            { icon: <Users className="w-6 h-6" />, num: ">15%", label: "Avg. Buyer Savings" },
          ].map(({ icon, num, label }, i) => (
            <AnimSection key={i}>
              <div className="flex flex-col items-center gap-2 text-white">
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-blue-300">{icon}</div>
                <div className="text-2xl font-bold">{num}</div>
                <div className="text-xs text-blue-200 font-medium">{label}</div>
              </div>
            </AnimSection>
          ))}
        </div>
      </div>

      {/* ── ABOUT ────────────────────────────────────────────────────────────── */}
      <section id="about" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50" aria-labelledby="about-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <div className="max-w-4xl">
              <span className="text-xs font-bold uppercase tracking-widest text-blue-600">Who We Are</span>
              <h2 id="about-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                About Metronavix
              </h2>
              <p className="mt-5 text-gray-600 text-lg leading-relaxed">
                Metronavix is a PropTech startup dedicated to making property ownership simpler, safer, and smarter.
                We bring together property sales support, quality inspections, builder QA/QC support, interior solutions,
                home loan assistance, and data‑driven advisory to help homeowners and investors get the most value from their real estate.
              </p>
              <p className="mt-4 text-gray-600 text-lg leading-relaxed">
                Our mission is to deliver trust and transparency to the property ecosystem in India — bridging the gap between
                developers, buyers, and service providers through technology.
              </p>
              <a href="#services" className="mt-6 inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all duration-200 group">
                See our services <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </a>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ── SERVICES ─────────────────────────────────────────────────────────── */}
      <section id="services" className="py-20 bg-white border-t border-gray-100" aria-labelledby="services-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-green-600">What We Do</span>
              <h2 id="services-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                Services that cover the full journey
              </h2>
              <p className="mt-3 text-gray-500 text-lg">From shortlisting to snag‑list closure — Metronavix is your single partner across the property lifecycle.</p>
            </div>
          </AnimSection>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <FileCheck />, title: "Property Sales Support", points: ["Thorough legal verification", "Documentation checks", "Transaction support", "Independent quality verification", "Regular construction updates"], color: "bg-gradient-to-br from-blue-500 to-cyan-500", delay: 0 },
              { icon: <ClipboardList />, title: "Quality Inspections", points: ["Independent snag & defect checks", "Professional pre-handover inspection", "Comprehensive quality assessment", "Detailed reporting with evidence"], color: "bg-gradient-to-br from-green-500 to-emerald-500", delay: 0.08 },
              { icon: <Building2 />, title: "Builder QA/QC Support", points: ["On-site quality assurance", "Quality control throughout construction", "Compliance maintenance", "Defect reduction strategies", "Customer satisfaction improvement"], color: "bg-gradient-to-br from-orange-500 to-red-500", delay: 0.16 },
              { icon: <Home />, title: "Interior Design & Modular", points: ["End-to-end design and execution", "Complete interior solutions", "Modular furniture systems", "Fixed budgets & timelines", "Warranty & AMC support"], color: "bg-gradient-to-br from-purple-500 to-pink-500", delay: 0.24 },
              { icon: <CreditCard />, title: "Home Loan Support", points: ["Best loan offers via bank partnerships", "Leading banks & financial institutions", "Easy financing solutions", "Transparent guidance", "Complete loan assistance"], color: "bg-gradient-to-br from-indigo-500 to-blue-500", delay: 0.32 },
              { icon: <Lightbulb />, title: "Advisory & Support", points: ["Property verification", "Due diligence support", "Micro-market insights", "Investment guidance", "Data-driven recommendations"], color: "bg-gradient-to-br from-yellow-500 to-orange-500", delay: 0.40 },
            ].map(({ icon, title, points, color, delay }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay }}>
                <Service icon={icon} title={title} points={points} colorClass={color} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US ───────────────────────────────────────────────────────────── */}
      <section id="why" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50 border-t border-gray-100" aria-labelledby="why-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <AnimSection>
              <span className="text-xs font-bold uppercase tracking-widest text-purple-600">Our Advantage</span>
              <h2 id="why-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Why buyers & builders choose Metronavix
              </h2>
              <p className="mt-3 text-gray-500 text-lg">Trust is engineered — not assumed. Our stack blends field expertise with data so decisions are faster and safer.</p>
              <ul className="mt-7 space-y-5" role="list">
                {[
                  { title: "Evidence‑first QC", desc: "Timestamped photos/videos, moisture readings, and laser measurements", icon: <CheckCircle2 className="w-5 h-5 text-purple-600" /> },
                  { title: "RERA‑aware workflows", desc: "Paperwork support aligned with state RERA guidelines", icon: <ShieldCheck className="w-5 h-5 text-purple-600" /> },
                  { title: "Data intelligence", desc: "Micro‑market pricing, registrations and comparable transactions", icon: <BarChart3 className="w-5 h-5 text-purple-600" /> },
                  { title: "End‑to‑end support", desc: "From site visit to handover, interiors, and post-occupancy", icon: <Zap className="w-5 h-5 text-purple-600" /> },
                ].map(({ title, desc, icon }, i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <div className="mt-0.5 shrink-0 w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center" aria-hidden="true">{icon}</div>
                    <div>
                      <p className="font-semibold text-gray-900">{title}</p>
                      <p className="text-gray-500 text-sm mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </AnimSection>
            <div className="grid sm:grid-cols-2 gap-4" aria-label="Performance metrics">
              {[
                { num: "75+", label: "QC checks per unit", color: "bg-gradient-to-br from-green-500 to-emerald-600" },
                { num: "> 200", label: "Snags resolved", color: "bg-gradient-to-br from-blue-500 to-cyan-600" },
                { num: "< 48h", label: "Avg. report TAT", color: "bg-gradient-to-br from-purple-500 to-pink-600" },
                { num: "> 15%", label: "Avg. buyer savings", color: "bg-gradient-to-br from-orange-500 to-red-600" },
              ].map(({ num, label, color }, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                  <KPI num={num} label={label} colorClass={color} />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROCESS ──────────────────────────────────────────────────────────── */}
      <section id="process" className="py-20 bg-gradient-to-br from-cyan-50 to-blue-50 border-t border-gray-100" aria-labelledby="process-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <span className="text-xs font-bold uppercase tracking-widest text-cyan-600">How It Works</span>
            <h2 id="process-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Your property journey in 4 steps
            </h2>
          </AnimSection>
          <div className="mt-12 grid md:grid-cols-4 gap-6 relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-blue-200 via-purple-200 to-pink-200" aria-hidden="true" />
            {[
              { step: "Discovery", desc: "Tell us your budget, location, and goals.", color: "bg-gradient-to-br from-blue-500 to-cyan-500" },
              { step: "Due Diligence", desc: "We validate titles, RERA status, and do on‑site checks.", color: "bg-gradient-to-br from-green-500 to-emerald-500" },
              { step: "QC & Snagging", desc: "We run 75+ checks and issue a clear, actionable snag report.", color: "bg-gradient-to-br from-purple-500 to-pink-500" },
              { step: "Handover & Beyond", desc: "We ensure defect closure, plan interiors, and assist with loans.", color: "bg-gradient-to-br from-orange-500 to-red-500" },
            ].map(({ step, desc, color }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, delay: i * 0.1 }}>
                <div className="rounded-2xl border border-gray-100 p-6 bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 rounded-full ${color} text-white flex items-center justify-center font-bold text-lg shadow-lg relative z-10`} aria-label={`Step ${i + 1}`}>
                    {i + 1}
                  </div>
                  <p className="mt-4 font-semibold text-gray-900">{step}</p>
                  <p className="text-sm text-gray-500 mt-1.5 leading-relaxed">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────────── */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-yellow-50 to-orange-50 border-t border-gray-100" aria-labelledby="testimonials-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <div className="max-w-3xl mb-12">
              <span className="text-xs font-bold uppercase tracking-widest text-yellow-600">Client Stories</span>
              <h2 id="testimonials-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">
                Proof our method works
              </h2>
              <p className="mt-3 text-gray-500 text-lg">What homeowners and builders say about partnering with Metronavix.</p>
            </div>
          </AnimSection>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { quote: "Seamless inspection to handover. The snag report helped us negotiate and saved us weeks of follow-up.", author: "Homeowner, Hyderabad", rating: 5 },
              { quote: "Finally a PropTech company that actually shows up on-site. Their QC report was detailed and professional.", author: "Property Investor, Hyderabad", rating: 5 },
              { quote: "They helped us close 12 builder defects we didn't even notice. Worth every rupee.", author: "Apartment Owner, Hyderabad", rating: 5 },
            ].map(({ quote, author, rating }, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.45, delay: i * 0.1 }}>
                <article className="rounded-2xl border border-yellow-100 bg-white p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                  <div className="flex gap-1 text-yellow-400" aria-label={`${rating} out of 5 stars`} role="img">
                    {[...Array(rating)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" aria-hidden="true" />)}
                  </div>
                  <blockquote className="mt-3 text-gray-700 leading-relaxed flex-1">"{quote}"</blockquote>
                  <footer className="mt-4 text-sm text-gray-400 font-medium">— {author}</footer>
                </article>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ───────────────────────────────────────────────────────── */}
      <section id="cta" className="py-20 bg-white" aria-labelledby="cta-heading">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimSection>
            <div className="rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-2xl relative overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 70% 50%, white 1px, transparent 1px)", backgroundSize: "24px 24px" }} aria-hidden="true" />
              <div className="relative">
                <h3 id="cta-heading" className="text-2xl sm:text-3xl font-bold">Ready to own better?</h3>
                <p className="mt-2 text-white/85 text-lg">Book a free 15‑minute consultation. We'll map your options and next steps.</p>
              </div>
              <a
                href="#contact"
                className="relative shrink-0 inline-flex items-center gap-2 rounded-xl px-6 py-3.5 bg-white text-purple-700 font-bold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-purple-600"
                aria-label="Book a free consultation with Metronavix"
              >
                <PhoneCall className="w-5 h-5" aria-hidden="true" /> Talk to Metronavix
              </a>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ── CONTACT ──────────────────────────────────────────────────────────── */}
      <section id="contact" className="py-20 border-t bg-gradient-to-br from-pink-50 to-rose-50" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <AnimSection>
              <span className="text-xs font-bold uppercase tracking-widest text-pink-600">Get In Touch</span>
              <h2 id="contact-heading" className="mt-2 text-3xl sm:text-4xl font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">
                Contact us
              </h2>
              <p className="mt-3 text-gray-500 text-lg">Tell us about your requirement and we'll get back within one business day.</p>
              <address className="mt-7 space-y-3 text-sm text-gray-600 not-italic">
                <p className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-pink-500 shrink-0" aria-hidden="true" />
                  <a href="mailto:founder@metronavix.com" className="hover:text-pink-600 transition-colors underline-offset-2 hover:underline">
                    founder@metronavix.com
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <PhoneCall className="w-4 h-4 text-pink-500 shrink-0" aria-hidden="true" />
                  <a href="tel:+91XXXXXXXXXX" className="hover:text-pink-600 transition-colors">+91‑XXXXXXXXXX</a>
                </p>
                <p className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-pink-500 shrink-0" aria-hidden="true" />
                  <span>Jubilee Hills, Hyderabad, Telangana 500033</span>
                </p>
              </address>
              <div className="mt-8 p-5 rounded-2xl bg-white border border-pink-100 shadow-sm">
                <p className="text-sm font-semibold text-gray-900 mb-2">Download our app</p>
                <a
                  href="https://play.google.com/store/apps/details?id=in.metronavixhub.android"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  aria-label="Download Metronavix on Google Play Store"
                >
                  <span className="text-lg" aria-hidden="true">📱</span> Available on Google Play
                </a>
              </div>
            </AnimSection>

            {/* Contact form */}
            <AnimSection>
              <div className="rounded-2xl border border-gray-100 p-6 sm:p-8 bg-white shadow-lg">
                {formStatus === "sent" ? (
                  <div className="flex flex-col items-center justify-center py-10 text-center" role="alert" aria-live="polite">
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Thank you!</h3>
                    <p className="text-gray-500 mt-2">We've received your enquiry and will get back within one business day.</p>
                    <button
                      onClick={() => setFormStatus("idle")}
                      className="mt-6 text-sm text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      Send another enquiry
                    </button>
                  </div>
                ) : (
                  <div role="form" aria-label="Contact Metronavix">
                    <h3 className="text-lg font-bold text-gray-900 mb-5">Send us an enquiry</h3>
                    <div className="grid gap-4">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Full name *" type="text" placeholder="Your name" value={formData.name} onChange={v => setFormData(p => ({ ...p, name: v }))} />
                        <Field label="Email *" type="email" placeholder="you@email.com" value={formData.email} onChange={v => setFormData(p => ({ ...p, email: v }))} />
                      </div>
                      <Field label="Phone" type="tel" placeholder="+91 98765 43210" value={formData.phone} onChange={v => setFormData(p => ({ ...p, phone: v }))} />
                      <div>
                        <label className="text-sm font-semibold text-gray-700" htmlFor="service-select">How can we help?</label>
                        <select
                          id="service-select"
                          value={formData.service}
                          onChange={e => setFormData(p => ({ ...p, service: e.target.value }))}
                          className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700"
                        >
                          <option value="">Select a service…</option>
                          <option>Property Sales Support</option>
                          <option>Quality Inspection</option>
                          <option>Builder QA/QC Support</option>
                          <option>Interior Design & Modular</option>
                          <option>Home Loan Support</option>
                          <option>Advisory & Support</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-700" htmlFor="message-field">Message</label>
                        <textarea
                          id="message-field"
                          value={formData.message}
                          onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                          className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px] resize-none"
                          placeholder="Tell us about your project…"
                        />
                      </div>
                      {formStatus === "error" && (
                        <p className="text-sm text-red-600" role="alert">Something went wrong. Please email us directly at founder@metronavix.com</p>
                      )}
                      <button
                        onClick={handleSubmit}
                        disabled={formStatus === "sending" || !formData.name || !formData.email}
                        className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3.5 bg-gradient-to-r from-pink-600 to-rose-600 text-white font-semibold hover:from-pink-700 hover:to-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                        aria-label="Submit your enquiry to Metronavix"
                      >
                        {formStatus === "sending" ? (
                          <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" aria-hidden="true" /> Sending…</>
                        ) : (
                          <><Rocket className="w-4 h-4" aria-hidden="true" /> Submit enquiry</>
                        )}
                      </button>
                      <p className="text-xs text-gray-400">
                        By submitting, you agree to our{" "}
                        <a href="/terms" className="underline hover:text-gray-600">Terms</a> and{" "}
                        <a href="/terms/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </AnimSection>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="py-14 border-t bg-gradient-to-br from-slate-900 to-gray-950 text-gray-300" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center" aria-hidden="true">
                  <Building2 className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-white text-lg">Metronavix</span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">PropTech for verified property discovery, quality inspections, and post‑handover support. Hyderabad → Pan‑India.</p>
            </div>
            <nav aria-label="Services navigation">
              <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Services</p>
              <ul className="space-y-2.5 text-sm">
                {["Property Sales Support", "Quality Inspections", "Builder QA/QC Support", "Interior Design & Modular", "Home Loan Support", "Advisory & Support"].map(s => (
                  <li key={s}><a href="#services" className="text-gray-400 hover:text-white transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded">{s}</a></li>
                ))}
              </ul>
            </nav>
            <nav aria-label="Company navigation">
              <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Company</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#about" className="text-gray-400 hover:text-white transition-colors duration-200">About</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-white transition-colors duration-200">Contact</a></li>
                <li><a href="/terms/privacy-policy" className="text-gray-400 hover:text-white transition-colors duration-200">Privacy Policy</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors duration-200">Terms & Conditions</a></li>
              </ul>
            </nav>
            <div>
              <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Get in touch</p>
              <address className="not-italic space-y-2 text-sm text-gray-400">
                <p><a href="mailto:founder@metronavix.com" className="hover:text-white transition-colors">founder@metronavix.com</a></p>
                <p>Jubilee Hills, Hyderabad</p>
                <p>Telangana 500033, India</p>
              </address>
              <a
                href="https://play.google.com/store/apps/details?id=in.metronavixhub.android"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
                aria-label="Get Metronavix on Google Play"
              >
                <span aria-hidden="true">📱</span> Google Play
              </a>
            </div>
          </div>
          <div className="mt-12 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
            <p>© {new Date().getFullYear()} Metronavix Private Limited. All rights reserved.</p>
            <p>PLOT NO.787, ROAD NO.36, 4TH FLOOR JUBILEE HILLS, Hyderabad – 500033</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Card({ icon, title, desc, colorClass = "bg-gradient-to-br from-blue-500 to-purple-600" }: CardProps) {
  return (
    <div className="rounded-2xl border border-gray-100 p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 bg-white/70">
      <div className={`w-10 h-10 rounded-xl ${colorClass} text-white flex items-center justify-center shadow-sm`} aria-hidden="true">{icon}</div>
      <p className="mt-3 font-semibold text-gray-900 text-sm">{title}</p>
      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{desc}</p>
    </div>
  );
}

function Service({ icon, title, points, colorClass = "bg-gradient-to-br from-blue-500 to-purple-600" }: ServiceProps) {
  return (
    <article className="rounded-2xl border border-gray-100 p-6 bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-purple-100 h-full flex flex-col">
      <div className={`w-12 h-12 rounded-xl ${colorClass} text-white flex items-center justify-center shadow-md`} aria-hidden="true">{icon}</div>
      <p className="mt-4 font-bold text-gray-900 text-lg">{title}</p>
      <ul className="mt-3 space-y-1.5 text-sm text-gray-500 flex-1" role="list">
        {points.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 shrink-0" aria-hidden="true" />
            {p}
          </li>
        ))}
      </ul>
    </article>
  );
}

function KPI({ num, label, colorClass = "bg-gradient-to-br from-blue-500 to-purple-600" }: KPIProps) {
  return (
    <div className={`rounded-2xl p-6 ${colorClass} text-white text-center hover:-translate-y-1 transition-all duration-300 shadow-lg`}>
      <div className="text-3xl font-bold">{num}</div>
      <div className="text-sm opacity-85 mt-1 font-medium">{label}</div>
    </div>
  );
}

function Field({ label, type, placeholder, value, onChange }: FieldProps) {
  const id = label.toLowerCase().replace(/[^a-z]/g, "-");
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold text-gray-700">{label}</label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
        required={label.includes("*")}
      />
    </div>
  );
}